import React, { useMemo } from 'react';
import { TextField, CircularProgress, Autocomplete, Box } from '@mui/material';
import { useBillingSearchServices } from './useBillingSearchServices';
import type { BillingService } from './serviceSearchHelper';

export interface ServiceSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  category?: string;
  patientId?: string | number;
  tariffId?: string | number;
  onSelectService?: (service: BillingService) => void;
  disabled?: boolean;
  label?: string;
}

const ServiceSearchField = ({ value, onChange, category, patientId, tariffId, onSelectService, disabled = true, label = 'Search By Service' }: ServiceSearchFieldProps) => {
  const { data, isLoading } = useBillingSearchServices({
    search: value,
    category,
    patientId,
    tariffId,
    debounceMs: 400,
  });

  const services = useMemo<BillingService[]>(() => (Array.isArray(data) ? data : []), [data]);

  return (
    <Box 
      component="section"
      position="relative"
      aria-label="Service search section"
    >
      <Autocomplete<BillingService>
        data-testid="service-search-autocomplete"
        fullWidth
        disabled={disabled}
        options={services}
        getOptionLabel={(option) => `${option.code} - ${option.name}`}
        filterOptions={(x) => x}
        loading={isLoading}
        inputValue={value}
        onInputChange={(_, newInputValue, reason) => {
          // Only trigger search when user is typing (reason === 'input') or clearing, not when autocomplete resets
          if (reason === 'input' || reason === 'clear') {
            onChange(newInputValue);
          }
        }}
        onChange={(_, newValue) => {
          if (newValue) {
            onSelectService?.(newValue);
            onChange(newValue.name);
          }
        }}
        renderOption={(props, option) => (
          <li 
            {...props} 
            data-testid={`service-option-${option.code}`}
            key={option.code}
          >
            {`${option.code} - ${option.name}`}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            data-testid="service-search-input"
            fullWidth
            size="small"
            label={label}
            aria-label="Search services by code or name"
            aria-describedby={isLoading ? 'service-search-loading' : undefined}
            slotProps={{
              input: {
                ...params.InputProps,
                role: 'searchbox',
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress 
                        size={18} 
                        data-testid="service-search-loading"
                        id="service-search-loading"
                        aria-label="Loading services"
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        aria-label="Service search autocomplete"
        slotProps={{
          listbox: {
            'data-testid': 'service-search-listbox',
            role: 'listbox',
            'aria-label': 'Available services',
          } as React.HTMLAttributes<HTMLUListElement>,
        }}
      />
    </Box>
  );
};

export default ServiceSearchField;
